import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    IconButton,
    DialogContent,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem,
    SelectChangeEvent,
    useTheme
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useCreateUserStore } from 'Application/store/useUserStore';
import { toast } from 'sonner';
import { UpdateUserRequest, UserRole } from 'Application/models';
import { retrieveEncryptedData } from 'Application/utils/AppSessionStorage';
import { ThemeMode } from 'config';

interface EditUserProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    user: UpdateUserRequest;
    id: number;
    agencyName?: string;
    selectedUserRole: UserRole;
}

const EditAgencyUser: React.FC<EditUserProps> = ({ isOpen, setIsOpen, user, id, agencyName, selectedUserRole }) => {
    const { editUser, getUserList, editUserRole, isLoading, isAgencyUsersLoading, getAgecyUsersList } = useCreateUserStore();
    const [userRole, setUserRole] = useState<UserRole>(selectedUserRole);
    const [formData, setFormData] = useState({
        firstName: '',
        otherNames: '',
        emailAddress: ''
    });

    const { palette } = useTheme();
    const modalTitleColor = palette.mode === ThemeMode.DARK ? '#A0AEC0' : '#4A5568';

    useEffect(() => {
        setFormData({
            firstName: user.firstName,
            otherNames: user.otherNames,
            emailAddress: user.emailAddress
        });

        // Sync role with the selected user role
        setUserRole(selectedUserRole);
    }, [id, user?.firstName, selectedUserRole]);

    const roleName = retrieveEncryptedData();

    const handleChange = (e: SelectChangeEvent<UserRole>) => {
        setUserRole(e.target.value as UserRole);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        const userData: UpdateUserRequest = {
            ...formData,
            firstName: formData.firstName,
            otherNames: formData.otherNames,
            emailAddress: formData.emailAddress
        };

        await editUser(id, userData);

        if (agencyName) {
            await editUserRole(id, userRole);
            await getAgecyUsersList(agencyName);
        } else {
            await getUserList();
        }
        toast.success('User updated successfully');
        setIsOpen(false);
        // clear the form data
        setFormData({
            firstName: '',
            otherNames: '',
            emailAddress: ''
        });
        setUserRole(UserRole.ROLE_AGENT); // Reset the role selection
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '70%',
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: modalTitleColor }}>Edit Agency User Details</span>
                    <IconButton onClick={handleClose}>
                        <ClearIcon />
                    </IconButton>
                </div>
                <div style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.875rem' }}>Provide the following details</div>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Full Name
                        </Typography>
                        <TextField
                            placeholder="Enter Full Name"
                            required
                            name="firstName"
                            variant="outlined"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            fullWidth
                            disabled={isLoading || isAgencyUsersLoading}
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Other Name
                        </Typography>
                        <TextField
                            placeholder="Enter Full Name"
                            required
                            name="otherNames"
                            variant="outlined"
                            value={formData.otherNames}
                            onChange={handleInputChange}
                            fullWidth
                            disabled={isLoading || isAgencyUsersLoading}
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Email
                        </Typography>
                        <TextField
                            placeholder="Enter Email"
                            required
                            name="emailAddress"
                            variant="outlined"
                            type="email"
                            value={formData.emailAddress}
                            onChange={handleInputChange}
                            fullWidth
                            disabled={isLoading || isAgencyUsersLoading}
                        />
                    </div>

                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Select A Role
                        </Typography>
                        <Select
                            value={userRole}
                            onChange={handleChange}
                            fullWidth
                            disabled={isLoading || isAgencyUsersLoading || roleName !== UserRole.ROLE_ADMINISTRATOR}
                        >
                            <MenuItem value={UserRole.ROLE_AGENCY_ADMIN}>Agency Admin</MenuItem>
                            <MenuItem value={UserRole.ROLE_BRANCH_MANAGER}>Branch manager</MenuItem>
                            <MenuItem value={UserRole.ROLE_AGENT}>Agent</MenuItem>
                        </Select>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: '#4A5568',
                                borderColor: '#4A5568',
                                backgroundColor: '#ffffff'
                            }}
                            disabled={isLoading} // Disable button while loading
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={{
                                backgroundColor: '#673ab7',
                                color: '#ffffff',
                                marginLeft: '1.5rem',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: '#673ab7',
                                    borderColor: '#673ab7'
                                },
                                '&.Mui-disabled': {
                                    color: '#ffffff',
                                    backgroundColor: '#98A2B3'
                                }
                            }}
                            disabled={isLoading || isAgencyUsersLoading}
                        >
                            {isLoading ? 'Saving...' : isAgencyUsersLoading ? 'Loading users...' : 'Save'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default EditAgencyUser;
